/**
 * Filter a list of items based on a search term across specified fields
 *
 * @param items - Array of items to filter
 * @param searchTerm - Search string to match
 * @param searchFields - Array of field names to search within
 * @param options - Configuration options
 * @returns Filtered array of items
 *
 * @example
 * Basic filtering:
 * ```typescript
 * const assets = [
 *   { id: '1', name: 'Asset One', description: 'First asset' },
 *   { id: '2', name: 'Asset Two', description: 'Second asset' }
 * ];
 *
 * const results = createSearchFilter(
 *   assets,
 *   'one',
 *   ['name', 'description'],
 *   { caseSensitive: false, minLength: 2 }
 * );
 * // Returns: [{ id: '1', name: 'Asset One', description: 'First asset' }]
 * ```
 *
 * @example
 * With custom search function (fallback):
 * ```typescript
 * const results = createSearchFilter(
 *   assets,
 *   'inspection',
 *   ['name', 'description'],
 *   {
 *     caseSensitive: false,
 *     minLength: 2,
 *     customSearchFn: (asset, searchTerm) => {
 *       // Fallback: search related forms
 *       const forms = assetForms.get(asset.id) || [];
 *       return forms.some(form =>
 *         form.name.toLowerCase().includes(searchTerm)
 *       );
 *     }
 *   }
 * );
 * ```
 */
export function createSearchFilter<T>(
  items: T[],
  searchTerm: string,
  searchFields: (keyof T)[],
  options: {
    caseSensitive?: boolean;
    minLength?: number;
    customSearchFn?: (item: T, searchTerm: string) => boolean;
  } = {},
): T[] {
  const { caseSensitive = false, minLength = 0, customSearchFn } = options;

  // Return all items if search term is empty or below minimum length
  if (!searchTerm || searchTerm.trim().length < minLength) {
    return items;
  }

  const term = caseSensitive ? searchTerm.trim() : searchTerm.trim().toLowerCase();

  return items.filter((item) => {
    // Fast path: check standard fields first
    const standardFieldMatch = searchFields.some((field) => {
      const value = item[field];

      // Skip null/undefined values
      if (value === null || value === undefined) {
        return false;
      }

      // Convert to string and apply case sensitivity
      const stringValue = String(value);
      const compareValue = caseSensitive ? stringValue : stringValue.toLowerCase();

      return compareValue.includes(term);
    });

    // Return immediately if standard fields match (fast path)
    if (standardFieldMatch) {
      return true;
    }

    // Fallback: use custom search function if provided
    if (customSearchFn) {
      return customSearchFn(item, term);
    }

    return false;
  });
}
